import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Avatar,
  Divider,
  List,
  ListItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Container,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  CreditCard as CreditCardIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon
} from '@mui/icons-material';
import { Typography, Button } from '../../../design-system';
import { useProfile } from '../hooks/useProfile';
import { EditProfileForm } from './EditProfileForm';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { SPACING_TOKENS, BORDER_RADIUS_TOKENS } from '../../../design-system/tokens/spacing';
import { useAuth } from '../../../hooks/useAuth';
import { asaasService } from '../../../services/asaasService';
import { CompleteProfileModal } from './CompleteProfileModal';
import { useToast } from '../../../hooks/useToast';

export const ProfileView: React.FC = () => {
  const { t } = useTranslation('profile');
  const { showError, showSuccess } = useToast();
  const { profile, loading, error, fetchProfile } = useProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const { authState } = useAuth();
  const [subLoading, setSubLoading] = useState(false);
  const [subscription, setSubscription] = useState<any | null>(null);
  const [accessGranted, setAccessGranted] = useState<boolean | null>(null);
  const [subError, setSubError] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const [showSubDetails, setShowSubDetails] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const getNormalizedPayments = (raw: any): any[] => {
    if (Array.isArray(raw?.data?.data)) return raw.data.data;
    if (Array.isArray(raw?.data?.items)) return raw.data.items;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    return [];
  };

  const getEarliestPendingPayment = (payments: any[]) => {
    return [...payments]
      .filter((payment: any) => ['PENDING', 'AWAITING_RISK_ANALYSIS'].includes(String(payment?.status || '').toUpperCase()))
      .filter((payment: any) => Number.isFinite(Date.parse(String(payment?.dueDate || ''))))
      .sort(
        (a: any, b: any) =>
          Date.parse(String(a?.dueDate || '')) - Date.parse(String(b?.dueDate || '')),
      )[0] || null;
  };


  const formatBusinessType = (value?: string) => {
    const v = (value || '').toLowerCase().trim();
    if (!v) return t('common:not_informed');
    const key = `business_type_map.${v}`;
    const translated = t(key);
    return translated === key ? (value || t('common:not_informed')) : translated;
  };

  const getPlanDisplayName = (description?: string | null) => {
    const raw = String(description || '').trim();
    if (!raw) return t('subscription.planDefault');
    return raw.replace(/\s*R\$\s*[\d,.]+.*/, '').trim() || t('subscription.planDefault');
  };

  useEffect(() => {
    let mounted = true;
    const loadSubscription = async () => {
      try {
        setSubLoading(true);
        setSubError(null);
        const email = authState.user?.email || '';
        if (!email) throw new Error('Usuário sem email');
        
        // 1. Verificar status de acesso (inclui cupons)
        const access: any = await asaasService.checkAccessByEmail(email);
        setAccessGranted(Boolean(access?.accessGranted));
        
        if (access.reasons?.includes('coupon_granted')) {
          const asaasOnly: any = await asaasService.checkAccessByEmailAsaasOnly(email);
          if (!mounted) return;

          if (asaasOnly?.subscription?.id) {
            const [detailResp, paymentsResp] = await Promise.all([
              asaasService.getSubscription(asaasOnly.subscription.id),
              asaasService.listSubscriptionPayments(asaasOnly.subscription.id),
            ]);
            if (!mounted) return;
            const detail: any = detailResp || {};
            const payments = getNormalizedPayments(paymentsResp);
            const earliestPendingPayment = getEarliestPendingPayment(payments);
            const shouldUsePendingChargeDate =
              Boolean(earliestPendingPayment?.dueDate) &&
              Boolean(detail?.nextDueDate) &&
              Date.parse(String(earliestPendingPayment?.dueDate || '')) <
                Date.parse(String(detail?.nextDueDate || ''));
            setSubscription({
              ...(detail || {}),
              coupon: access.coupon,
              reasons: access.reasons,
              accessGranted: true,
              firstPendingChargeDate: shouldUsePendingChargeDate ? earliestPendingPayment?.dueDate : null,
              firstPendingChargeId: shouldUsePendingChargeDate ? earliestPendingPayment?.id : null,
            });
          } else {
            setSubscription({
              ...access.subscription,
              coupon: access.coupon,
              reasons: access.reasons,
              status: 'ACTIVE',
              accessGranted: true,
            });
          }
        } else if (access.subscription?.id) {
          // Se for assinatura normal do Asaas, buscamos os detalhes completos
          const [detailResp, paymentsResp] = await Promise.all([
            asaasService.getSubscription(access.subscription.id),
            asaasService.listSubscriptionPayments(access.subscription.id),
          ]);
          if (!mounted) return;
          const detail: any = detailResp || {};
          const payments = getNormalizedPayments(paymentsResp);
          const earliestPendingPayment = getEarliestPendingPayment(payments);
          setSubscription({
            ...(detail || {}),
            firstPendingChargeDate: earliestPendingPayment?.dueDate || null,
            firstPendingChargeId: earliestPendingPayment?.id || null,
          });
        } else {
          if (!mounted) return;
          setSubscription(null);
        }
      } catch (e: any) {
        if (!mounted) return;
        setSubError(e?.message || t('subscription.loadError'));
      } finally {
        if (mounted) setSubLoading(false);
      }
    };
    loadSubscription();
    return () => { mounted = false; };
  }, [authState.user?.email]);
  
  const isExpired = String(subscription?.status || '').toUpperCase() === 'EXPIRED';
  const hasCouponGranted = Boolean(subscription?.reasons?.includes('coupon_granted'));
  const hasRealSubscriptionId = Boolean(String(subscription?.id || '').trim());
  const planName = getPlanDisplayName(subscription?.description);
  const planValue = Number(subscription?.value || 30);
  const accessUntilDate = hasCouponGranted
    ? subscription?.coupon?.effectiveExpiry || subscription?.coupon?.expirationDate
    : subscription?.endDate;
  const formattedAccessUntil = accessUntilDate ? new Date(accessUntilDate).toLocaleDateString('pt-BR') : '—';
  const nextChargeDate = subscription?.firstPendingChargeDate || subscription?.nextDueDate || null;
  const formattedNextCharge = nextChargeDate
    ? new Date(nextChargeDate).toLocaleDateString('pt-BR')
    : '—';
  const showTrialBadge = hasCouponGranted;
  const canCancelSubscription = hasRealSubscriptionId && !isExpired && !subscription?.endDate;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
        <Typography variant="body1" sx={{ ml: 2 }}>{t('common:status.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!profile) {
    if (isEditing) {
    return (
      <EditProfileForm
        profile={null}
        onCancel={() => setIsEditing(false)}
        onSave={async () => {
          setIsEditing(false);
          await fetchProfile();
        }}
      />
    );
  }
    
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column' }}>
        <Avatar sx={{ width: 80, height: 80, bgcolor: DESIGN_TOKENS.colors.primary[500], mb: 2 }}>
          <PersonIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h5" sx={{ mb: 1, color: DESIGN_TOKENS.colors.text.primary }}>{t('profile_not_found')}</Typography>
        <Typography variant="body1" sx={{ mb: 3, color: DESIGN_TOKENS.colors.text.secondary }}>{t('create_new_profile')}</Typography>
        <Button variant="primary" onClick={() => setShowProfileModal(true)}>{t('create_profile')}</Button>
        
        <CompleteProfileModal 
          open={showProfileModal} 
          onClose={() => setShowProfileModal(false)}
          onSuccess={async () => {
            setShowProfileModal(false);
            await fetchProfile();
          }}
        />
      </Box>
    );
  }

  if (isEditing) {
    return (
      <EditProfileForm
        profile={profile}
        onCancel={() => setIsEditing(false)}
        onSave={async () => {
          setIsEditing(false);
          await fetchProfile();
        }}
      />
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#FAFAFA', // Minimalist light gray background
      py: { xs: SPACING_TOKENS.semantic.md, md: SPACING_TOKENS.semantic.xl },
      px: { xs: SPACING_TOKENS.semantic.md, md: 0 }
    }}>
      <Container maxWidth="lg" disableGutters>

        {/* Main Content */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: SPACING_TOKENS.semantic.xl }}>
          {/* Left Panel - Profile Overview */}
          <Box sx={{ width: { xs: '100%', md: '30%' } }}>
            <Box sx={{ 
              bgcolor: 'white', 
              borderRadius: 4, 
              p: 4, 
              boxShadow: '0px 4px 20px rgba(0,0,0,0.03)', // Subtle shadow
              border: '1px solid rgba(0,0,0,0.05)',
              textAlign: { xs: 'left', md: 'left' }
            }}>
              {/* Profile Avatar & Basic Info */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    sx={{
                      width: 100,
                      height: 100,
                      mb: 2,
                      bgcolor: DESIGN_TOKENS.colors.primary[50],
                      color: DESIGN_TOKENS.colors.primary[600],
                      fontSize: '2.5rem',
                      fontWeight: 500,
                      border: `4px solid white`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                  >
                    {profile.username?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 0,
                    width: 16,
                    height: 16,
                    bgcolor: '#10B981', // Success green
                    borderRadius: '50%',
                    border: '3px solid white'
                  }} />
                </Box>
                
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: '#111827' }}>
                  {profile.username}
                </Typography>
                
                {profile.business_info?.company_name && (
                  <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                    {profile.business_info.company_name}
                  </Typography>
                )}
              </Box>

              {/* Quick Stats - Minimalist */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                gap: 4, 
                mb: 4,
                py: 3,
                borderTop: '1px solid #F3F4F6',
                borderBottom: '1px solid #F3F4F6'
              }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', lineHeight: 1 }}>100%</Typography>
                  <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>Completo</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: '#F3F4F6' }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', lineHeight: 1 }}>5</Typography>
                  <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>Conexões</Typography>
                </Box>
              </Box>

              {/* Quick Actions - Clean List */}
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" sx={{ 
                  textTransform: 'uppercase', 
                  color: '#9CA3AF', 
                  fontWeight: 600, 
                  letterSpacing: '0.05em',
                  mb: 2,
                  display: 'block'
                }}>
                  Menu
                </Typography>
                <List sx={{ p: 0 }}>
                 {/* Reusable Action Item Component */}
                 {[
                   { icon: SecurityIcon, label: t('quickActions.security') },
                   { icon: NotificationsIcon, label: t('quickActions.notifications') },
                   { icon: VisibilityIcon, label: t('quickActions.privacy') }
                 ].map((action, index) => (
                   <ListItem 
                    key={index} 
                    disablePadding 
                    sx={{ mb: 1.5 }}
                   >
                     <Box sx={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       cursor: 'pointer',
                       width: '100%',
                       p: 1.5,
                       borderRadius: 2,
                       transition: 'all 0.2s',
                       '&:hover': { bgcolor: '#F9FAFB', color: DESIGN_TOKENS.colors.primary[600] }
                     }}>
                       <action.icon sx={{ fontSize: 20, color: 'inherit', mr: 2, opacity: 0.7 }} />
                       <Typography variant="body2" sx={{ fontWeight: 500 }}>{action.label}</Typography>
                     </Box>
                   </ListItem>
                 ))}
               </List>
              </Box>
            </Box>
          </Box>

          {/* Right Panel - Detailed Information */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* Subscription Section - Clean & Prominent */}
              <Box sx={{ 
                bgcolor: 'white', 
                borderRadius: 4, 
                p: 4, 
                boxShadow: '0px 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: DESIGN_TOKENS.colors.primary[50], color: DESIGN_TOKENS.colors.primary[500] }}>
                      <CreditCardIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>{t('subscription.title')}</Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280' }}>Gerencie seu plano e cobranças</Typography>
                    </Box>
                  </Box>
                </Box>
                
                {subLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                  </Box>
                ) : !subLoading && subError ? (
                  <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>{subError}</Alert>
                ) : !subLoading && !subError && subscription ? (
                  <Box sx={{ bgcolor: '#F9FAFB', borderRadius: 3, p: 3 }}>
                     <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: { xs: 2, sm: 0 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: '50%', 
                            bgcolor: DESIGN_TOKENS.colors.primary[50], 
                            color: DESIGN_TOKENS.colors.primary[500],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CreditCardIcon sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
                              {planName}
                            </Typography>
                            {showTrialBadge && (
                              <Chip
                                label={t('subscription.trialActiveLabel')}
                                size="small"
                                sx={{
                                  mt: 0.75,
                                  bgcolor: '#ECFDF5',
                                  color: '#059669',
                                  fontWeight: 700,
                                  borderRadius: 1,
                                  height: 24,
                                }}
                              />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography variant="h5" sx={{ color: '#111827', fontWeight: 700 }}>
                                {`R$ ${Number.isFinite(planValue) ? planValue.toFixed(0) : '30'}`}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6B7280' }}>/mês</Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                          <Chip
                            label={(accessGranted ?? false) ? t('common:status.active') : (subscription?.status || t('common:status.inactive'))}
                            size="small"
                            sx={{ 
                              bgcolor: (accessGranted ?? false) ? '#ECFDF5' : '#FEF3C7', 
                              color: (accessGranted ?? false) ? '#059669' : '#92400E', 
                              fontWeight: 600,
                              borderRadius: 1,
                              height: 24
                            }}
                          />
                          <MuiButton
                            size="small"
                            onClick={() => setShowSubDetails(!showSubDetails)}
                            endIcon={showSubDetails ? <ArrowUpIcon /> : <ArrowDownIcon />}
                            sx={{ textTransform: 'none', color: '#6B7280' }}
                          >
                            {showSubDetails ? 'Menos detalhes' : 'Mais detalhes'}
                          </MuiButton>
                        </Box>
                     </Box>
                     
                     {showSubDetails && (
                       <>
                         <Divider sx={{ my: 2, borderColor: '#E5E7EB' }} />
                         
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isExpired ? '1fr' : '1fr 1fr' }, gap: 2 }}>
                          {showTrialBadge && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>{t('subscription.trialUntil')}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                                {formattedAccessUntil}
                              </Typography>
                            </Box>
                          )}
                          {!isExpired && (
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>{t('subscription.nextCharge')}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                                 {formattedNextCharge}
                              </Typography>
                            </Box>
                          )}
                           <Box>
                             <Typography variant="caption" sx={{ color: '#9CA3AF' }}>{t('subscription.id')}</Typography>
                             <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151', fontFamily: 'monospace' }}>
                               {subscription?.id || '—'}
                             </Typography>
                           </Box>
                         </Box>
                         
                         {canCancelSubscription && (
                             <Box sx={{ mt: 3 }}>
                                <MuiButton
                                 variant="text"
                                 size="small"
                                 sx={{ color: '#EF4444', p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                                 onClick={() => setCancelModalOpen(true)}
                               >
                                 {t('subscription.cancelAction')}
                               </MuiButton>
                             </Box>
                          )}
                         {hasRealSubscriptionId && subscription?.endDate && (
                             <Box sx={{ mt: 3 }}>
                               <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600 }}>
                                 {t('subscription.cancelledAccessUntil', { date: formattedAccessUntil })}
                               </Typography>
                             </Box>
                          )}
                       </>
                     )}
                  </Box>
                ) : (
                  <Alert severity="info" variant="outlined">Nenhuma assinatura ativa.</Alert>
                )}
              </Box>

              {/* Modal de Cancelamento */}
              <Dialog
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">
                  {"Cancelar Assinatura?"}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    {t('subscription.cancelConfirm')}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <MuiButton onClick={() => setCancelModalOpen(false)} color="primary">
                    Fechar
                  </MuiButton>
                  <MuiButton 
                    onClick={async () => {
                      setCancelModalOpen(false);
                      try {
                        setCancelLoading(true);
                        const response: any = await asaasService.scheduleCancelAtPeriodEnd(subscription?.id as string);
                        const endDate = String(
                          response?.endDate || response?.data?.endDate || accessUntilDate || subscription?.nextDueDate || '',
                        ).trim();
                        const nextChargeDate = response?.nextChargeDate === null
                          ? null
                          : String(response?.nextChargeDate || response?.data?.nextChargeDate || subscription?.firstPendingChargeDate || '').trim() || null;
                        setSubscription((current: any) => current ? {
                          ...current,
                          endDate: endDate || current.endDate,
                          firstPendingChargeDate: nextChargeDate,
                        } : current);
                        showSuccess(t('subscription.cancelSuccess'));
                      } catch(e: any) { 
                        showError(e?.message || t('subscription.cancelError')); 
                      } finally { 
                        setCancelLoading(false); 
                      }
                    }} 
                    color="error" 
                    disabled={cancelLoading}
                    autoFocus
                  >
                    Continuar cancelamento
                  </MuiButton>
                </DialogActions>
              </Dialog>

              {/* Personal & Business Info - Unified Card */}
              <Box sx={{ 
                bgcolor: 'white', 
                borderRadius: 4, 
                p: 4, 
                boxShadow: '0px 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#F3F4F6', color: '#4B5563' }}>
                      <PersonIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>Informações</Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280' }}>Dados pessoais e comerciais</Typography>
                    </Box>
                  </Box>
                  <Button 
                    variant="secondary" 
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Editar
                  </Button>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
                  {/* Personal Column */}
                  <Box>
                    <Typography variant="body1" sx={{ 
                      color: '#9CA3AF', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em', 
                      mb: 3, 
                      fontWeight: 600 
                    }}>
                      Pessoal
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Email</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                            {profile.contact_details?.email || '—'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Telefone</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                            {profile.contact_details?.phone || '—'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Localização</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                            {profile.contact_details?.address || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Business Column */}
                  <Box>
                    <Typography variant="body1" sx={{ 
                      color: '#9CA3AF', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em', 
                      mb: 3, 
                      fontWeight: 600 
                    }}>
                      Comercial
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Empresa</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', mt: 0.5 }}>
                          {profile.business_info?.company_name || '—'}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Tipo de Negócio</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', mt: 0.5 }}>
                          {formatBusinessType(profile.business_info?.business_type)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Website</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', mt: 0.5 }}>
                          {profile.business_info?.website || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Preferences - Simple Horizontal */}
              <Box sx={{ 
                bgcolor: 'white', 
                borderRadius: 4, 
                p: 3, 
                boxShadow: '0px 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#F3F4F6', color: '#4B5563' }}>
                       <SettingsIcon fontSize="small" />
                     </Box>
                     <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827' }}>Preferências do Sistema</Typography>
                  </Box>
                 
                 <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                       <LanguageIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                       <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile.preferences?.language || 'pt'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                       <ScheduleIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                       <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile.preferences?.timezone || 'America/Sao_Paulo'}</Typography>
                    </Box>
                 </Box>
              </Box>

            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
